var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { setJwtToken, setRefreshToken, setContextData, setViewType, isRegistered, setToken, clearAll, setError, } from './authslice';
import { workspaceApiSlice } from '../app/workspaces/service/api';
import NatsClient from './natsclient';
import { expiresIn, isExpired } from '../common/util';
import { DataService } from '../app/configure/service/deviceservice';
import { Constants } from '../app/configure/models/constants';
import { getTenantFeatureConfig } from '../common/util/tenant-feature-config';
// Add error keys enum
export var AuthErrorKey;
(function (AuthErrorKey) {
    AuthErrorKey["REFRESH_API_401"] = "REFRESH_API_401";
    AuthErrorKey["REFRESH_API_OTHER"] = "REFRESH_API_OTHER";
    AuthErrorKey["CONTEXT_API_401"] = "CONTEXT_API_401";
    AuthErrorKey["CONTEXT_API_OTHER"] = "CONTEXT_API_OTHER";
})(AuthErrorKey || (AuthErrorKey = {}));
const LOCAL_STORAGE_SMARTLOOK_TENANT_ID = 'smartlook_tenant_id';
const SMARTLOOK_INIT_SCRIPT_PATH = '/smartlook-init.js';
const SMARTLOOK_INIT_SCRIPT_ID = 'smartlook-init-script';
// Startup must not remain on the branded loader indefinitely when a local
// backend or NATS endpoint is unavailable. The existing error screen gives the
// user a recoverable path (retry/clear cache) once this deadline is reached.
const AUTH_BOOTSTRAP_TIMEOUT_MS = 15000;
const withTimeout = (promise, message) => new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
        reject(Object.assign(new Error(message), { status: 504 }));
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);
    promise.then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
    }, (error) => {
        clearTimeout(timeoutId);
        reject(error);
    });
});
let smartlookScriptLoadPromise = null;
const loadSmartlookInitScript = () => {
    if (smartlookScriptLoadPromise) {
        return smartlookScriptLoadPromise;
    }
    if (typeof document === 'undefined' ||
        document.getElementById(SMARTLOOK_INIT_SCRIPT_ID)) {
        return Promise.resolve();
    }
    smartlookScriptLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SMARTLOOK_INIT_SCRIPT_ID;
        script.src = SMARTLOOK_INIT_SCRIPT_PATH;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            smartlookScriptLoadPromise = null;
            reject(new Error('Failed to load Smartlook init script'));
        };
        document.head.appendChild(script);
    }).catch((error) => {
        console.warn(error);
        throw error;
    });
    return smartlookScriptLoadPromise;
};
const initializeSmartlookForTenant = (tenantId) => __awaiter(void 0, void 0, void 0, function* () {
    if (typeof window === 'undefined' ||
        typeof document === 'undefined' ||
        tenantId === undefined ||
        tenantId === null) {
        return;
    }
    const normalizedTenantId = tenantId.toString();
    if (!normalizedTenantId) {
        return;
    }
    const { smartlookTenants } = yield getTenantFeatureConfig();
    if (!smartlookTenants.has(normalizedTenantId)) {
        window.localStorage.removeItem(LOCAL_STORAGE_SMARTLOOK_TENANT_ID);
        return;
    }
    window.localStorage.setItem(LOCAL_STORAGE_SMARTLOOK_TENANT_ID, normalizedTenantId);
    yield loadSmartlookInitScript();
});
const authMiddleware = (store) => {
    let intervalId = null;
    const dataService = new DataService();
    return (next) => (action) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const result = next(action);
        const dispatch = store.dispatch;
        const { jwtToken, refreshToken } = store.getState().auth;
        if (!intervalId && jwtToken && refreshToken) {
            console.log('Creating JWT refresh interval - initial tokens available');
            intervalId = setInterval(() => __awaiter(void 0, void 0, void 0, function* () {
                // Get current tokens from store on each interval check
                const currentAuth = store.getState().auth;
                const currentJwtToken = currentAuth.jwtToken;
                const currentRefreshToken = currentAuth.refreshToken;
                if (!currentRefreshToken || isExpired(currentRefreshToken)) {
                    console.error(`Refresh token is expired and refreshToken ${currentRefreshToken ? 'is available' : 'is not available'}`);
                    return;
                }
                if (!currentJwtToken ||
                    expiresIn(currentJwtToken, 60) ||
                    isExpired(currentJwtToken)) {
                    console.log(`JWT token is expired or not available. Going to refresh JWT token. Current token expiry check: expired=${isExpired(currentJwtToken)}, expiresIn60=${expiresIn(currentJwtToken, 60)}`);
                    try {
                        const refreshResult = (yield dispatch(workspaceApiSlice.endpoints.refresh.initiate({
                            jwt: currentJwtToken,
                            refreshToken: currentRefreshToken,
                        })).unwrap());
                        if (refreshResult) {
                            console.log('JWT token refreshed successfully, updating NATS connection...');
                            console.log(`Old JWT token expiry: ${currentJwtToken ? 'available' : 'null'}, New JWT token expiry will be updated`);
                            store.dispatch(setJwtToken(refreshResult.jwt));
                            store.dispatch(setRefreshToken(refreshResult.refreshToken));
                            console.log('Redux state updated with new JWT and refresh tokens');
                            // Update NATS connection with new JWT token and refresh token
                            try {
                                const success = yield NatsClient.updateJWTAndReconnect(refreshResult.jwt, refreshResult.refreshToken);
                                if (success) {
                                    console.log('NATS connection successfully updated with new JWT token');
                                }
                                else {
                                    console.error('Failed to update NATS connection with new JWT token');
                                }
                            }
                            catch (natsError) {
                                console.error('Error updating NATS connection with new JWT token:', natsError);
                            }
                        }
                        else {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_OTHER,
                                showClearCache: true,
                                errorCode: undefined,
                            }));
                        }
                    }
                    catch (error) {
                        const err = error;
                        if (err && err.status === 401) {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_401,
                                showClearCache: true,
                            }));
                        }
                        else {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_OTHER,
                                showClearCache: true,
                                errorCode: err && err.status ? err.status : undefined,
                            }));
                        }
                    }
                }
            }), 
            //5000
            5 * 60 * 1000);
        }
        const act = action;
        if (act.type === isRegistered.type) {
            const registered = act.payload;
            try {
                if (!registered) {
                    console.log('No public key found. Going to register public key');
                    const newPublicKey = NatsClient.getPublicKey();
                    const registerResult = (yield withTimeout(dispatch(workspaceApiSlice.endpoints.register.initiate({
                        publicKey: newPublicKey,
                        tenantMask: dataService.getTenantIdMasked(),
                        deviceId: dataService.getDeviceId(),
                    })).unwrap(), 'Public key registration timed out'));
                    console.log('Public key registered successfully :', registerResult);
                    store.dispatch(isRegistered(true));
                }
            }
            catch (error) {
                const registerError = error;
                const registerStatus = (_a = registerError === null || registerError === void 0 ? void 0 : registerError.status) !== null && _a !== void 0 ? _a : (_b = registerError === null || registerError === void 0 ? void 0 : registerError.error) === null || _b === void 0 ? void 0 : _b.status;
                console.error('Error getting registerResult Data:', error);
                // A 409 means this public key is already registered on the active
                // backend, so it is safe to continue with context generation.
                if (registerStatus === 409) {
                    store.dispatch(isRegistered(true));
                }
                else {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_OTHER,
                        showClearCache: true,
                        errorCode: registerStatus || 500,
                    }));
                }
            }
        }
        if (act.type === setToken.type) {
            let bootstrapStage = 'context';
            try {
                const token = store.getState().auth.token
                    ? store.getState().auth.token
                    : dataService.getDeviceToken();
                if (!token) {
                    throw Object.assign(new Error('Device token is missing'), {
                        status: 401,
                    });
                }
                const contextResultUnWrapped = (yield withTimeout(dispatch(workspaceApiSlice.endpoints.context.initiate(token)), 'Context request timed out'));
                const contextResult = contextResultUnWrapped === null || contextResultUnWrapped === void 0 ? void 0 : contextResultUnWrapped.data;
                if ('error' in contextResultUnWrapped) {
                    if ((contextResultUnWrapped === null || contextResultUnWrapped === void 0 ? void 0 : contextResultUnWrapped.error) &&
                        'status' in contextResultUnWrapped.error &&
                        contextResultUnWrapped.error.status === 403) {
                        dataService.removeCAuthToken();
                        const encodedRedirectUri = encodeURIComponent(`${window.location.protocol + '//' + `${window.location.host}?token=` + dataService.getDeviceToken()}`);
                        window.location.replace(`${Constants.CENTRAL_AUTH_REDIRECT_URL}&redirect_uri=${encodedRedirectUri}`);
                        return;
                    }
                }
                if (contextResult) {
                    store.dispatch(setContextData(contextResult));
                    void initializeSmartlookForTenant(contextResult.tenantId);
                    // Defer viewType until the locationHierarchy NATS message has populated
                    // the canonical Spaces slice.
                    const connectToServer = () => __awaiter(void 0, void 0, void 0, function* () {
                        const { host, streamName, port, subscriptionInfo, viewType, jwt, refreshToken, } = contextResult;
                        store.dispatch(setJwtToken(jwt));
                        store.dispatch(setRefreshToken(refreshToken));
                        bootstrapStage = 'realtime-connect';
                        console.log('Going to connect to server with', host, port, streamName);
                        const connected = yield withTimeout(NatsClient.connectToNatServer(`${host}:${port}`, jwt, streamName, refreshToken, false), 'Realtime service connection timed out');
                        if (!connected) {
                            throw Object.assign(new Error('Unable to connect to the realtime service'), { status: 503 });
                        }
                        bootstrapStage = 'location-message';
                        yield withTimeout(NatsClient.getMessageForSubject(subscriptionInfo.locationHierarchy), 'Location data request timed out');
                        const subjects = subscriptionInfo.analytics.concat(subscriptionInfo.locationHierarchy, subscriptionInfo.events);
                        NatsClient.addGlobalSubscriptions(subjects);
                        // Wait until spacesData.location.children is set by the `lh` message.
                        const checkLocationChildren = () => {
                            const loc = store.getState().spacesData.location;
                            return Array.isArray(loc === null || loc === void 0 ? void 0 : loc.children);
                        };
                        const waitForLocationChildren = () => __awaiter(void 0, void 0, void 0, function* () {
                            const deadline = Date.now() + AUTH_BOOTSTRAP_TIMEOUT_MS;
                            while (!checkLocationChildren()) {
                                if (Date.now() >= deadline) {
                                    throw Object.assign(new Error('Location data did not arrive from the realtime service'), { status: 504 });
                                }
                                yield new Promise((resolve) => setTimeout(resolve, 20));
                            }
                        });
                        bootstrapStage = 'location-state';
                        yield waitForLocationChildren();
                        store.dispatch(setViewType(viewType));
                    });
                    // perform connection and then viewType dispatch
                    yield connectToServer();
                }
                else {
                    const contextStatus = (_c = contextResultUnWrapped === null || contextResultUnWrapped === void 0 ? void 0 : contextResultUnWrapped.error) === null || _c === void 0 ? void 0 : _c.status;
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: contextStatus === 401
                            ? AuthErrorKey.CONTEXT_API_401
                            : AuthErrorKey.CONTEXT_API_OTHER,
                        showClearCache: true,
                        errorCode: contextStatus || 500,
                    }));
                }
            }
            catch (error) {
                const err = error;
                console.error('Context bootstrap failed at stage:', bootstrapStage, error);
                if (err && err.status === 401) {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_401,
                        showClearCache: true,
                    }));
                }
                else {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_OTHER,
                        showClearCache: true,
                        errorCode: err && err.status ? err.status : undefined,
                    }));
                }
            }
        }
        if (act.type === clearAll.type) {
            if (intervalId)
                clearInterval(intervalId);
            intervalId = null;
        }
        return result;
    });
};
export default authMiddleware;
